import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  getPromotionTemplates,
  createPromotionTemplate,
  copyPromotionTemplate,
} from 'utils/teachingTeamApi'
import { useCopyToClipboard } from 'utils'

const PLATFORMS = [
  { key: 'threads', label: 'Threads', emoji: '🧵' },
  { key: 'fb', label: 'FB', emoji: '👥' },
  { key: 'ig', label: 'IG', emoji: '📸' },
  { key: 'line', label: 'LINE', emoji: '💬' },
  { key: 'ptt', label: 'PTT', emoji: '📋' },
]

const buildFinalLink = (base, templateId, platformKey) => {
  if (!base || !platformKey) return ''
  const campaign = templateId ? `promo-${templateId}` : 'promo-new'
  const sep = base.includes('?') ? '&' : '?'
  return `${base}${sep}utm_source=${platformKey}&utm_campaign=${campaign}`
}

export default function PromotionLinkModal({ isOpen, onClose, baseUrl, teamId, inviteCode, colors, translations = {} }) {
  const t = translations
  const primary = colors?.primary || '#3366FF'
  const bg = colors?.background || '#E6F0FF'

  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const [inputLabel, setInputLabel] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [platform, setPlatform] = useState(null)
  const [copying, setCopying] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [copyError, setCopyError] = useState(false)
  const [copyDone, setCopyDone] = useState(false)
  const comboboxRef = useRef(null)

  const { copyToClipboard, isCopied } = useCopyToClipboard(2000)

  // Build base URL with ref
  const baseWithRef = (() => {
    if (!baseUrl) return ''
    if (!inviteCode) return baseUrl
    if (baseUrl.includes('ref=')) return baseUrl
    const sep = baseUrl.includes('?') ? '&' : '?'
    return `${baseUrl}${sep}ref=${inviteCode}`
  })()

  const finalLink = buildFinalLink(baseWithRef, selectedTemplate?.id || null, platform)

  // Fetch templates on open
  const fetchTemplates = useCallback(async () => {
    if (!teamId) return
    setLoading(true)
    setError(false)
    try {
      const data = await getPromotionTemplates(teamId)
      setTemplates(data.templates || [])
    } catch {
      setError(true)
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }, [teamId])

  useEffect(() => {
    if (isOpen) {
      fetchTemplates()
      setInputLabel('')
      setSelectedTemplate(null)
      setPlatform(null)
      setCopying(false)
      setCopyDone(false)
      setDropdownOpen(false)
    }
  }, [isOpen, fetchTemplates])

  // ESC to close
  useEffect(() => {
    if (!isOpen) return
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  // Lock body scroll
  useEffect(() => {
    if (!isOpen) return
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return
    const handler = (e) => {
      if (comboboxRef.current && !comboboxRef.current.contains(e.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen])

  const filteredTemplates = isTyping && inputLabel.trim()
    ? templates.filter((tmpl) => tmpl.label.toLowerCase().includes(inputLabel.toLowerCase()))
    : templates

  const handleSelectTemplate = (tmpl) => {
    setInputLabel(tmpl.label)
    setSelectedTemplate(tmpl)
    setIsTyping(false)
    setDropdownOpen(false)
  }

  const handleInputChange = (e) => {
    setInputLabel(e.target.value)
    setSelectedTemplate(null)
    setIsTyping(true)
    setDropdownOpen(true)
  }

  const handleInputFocus = () => {
    setIsTyping(false)
    setDropdownOpen(true)
  }

  // Copy tracked link
  const handleCopyTracked = async () => {
    if (!platform || (!selectedTemplate && !inputLabel.trim())) return
    setCopying(true)
    try {
      let templateId = selectedTemplate?.id || null

      if (!selectedTemplate && inputLabel.trim()) {
        const created = await createPromotionTemplate(teamId, { label: inputLabel.trim() })
        templateId = created.id
        setSelectedTemplate(created)
        await copyPromotionTemplate(teamId, templateId, baseWithRef)
      } else if (selectedTemplate) {
        await copyPromotionTemplate(teamId, selectedTemplate.id, baseWithRef)
      }

      const link = buildFinalLink(baseWithRef, templateId, platform)
      const ok = await copyToClipboard(link)
      if (ok) {
        setCopyDone(true)
        setTimeout(onClose, 800)
      } else {
        setCopyError(true)
        setTimeout(() => setCopyError(false), 3000)
      }
    } catch {
      setCopyError(true)
      setTimeout(() => setCopyError(false), 3000)
    } finally {
      setCopying(false)
    }
  }

  // Copy plain link (with ref, no UTM)
  const handleCopyPlain = async () => {
    const ok = await copyToClipboard(baseWithRef)
    if (ok) {
      setCopyDone(true)
      setTimeout(onClose, 800)
    } else {
      setCopyError(true)
      setTimeout(() => setCopyError(false), 3000)
    }
  }

  if (!isOpen) return null

  const canCopyTracked = platform && (selectedTemplate || inputLabel.trim())

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] lg:p-4"
      onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose() }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:max-w-lg lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-hidden flex flex-col">
        {/* Mobile header */}
        <div className="flex items-center px-4 py-3 border-b lg:hidden">
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 cursor-pointer mr-3"
            aria-label="Back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="font-semibold text-gray-900 text-sm">
            {t.title || '推廣連結'}
          </span>
        </div>

        {/* Desktop header */}
        <div
          className="hidden lg:flex items-center justify-between px-6 py-4 border-b shrink-0"
          style={{ backgroundColor: bg, color: primary }}
        >
          <span className="font-semibold text-sm">
            {t.title || '推廣連結'}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-black/5 cursor-pointer"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-y-auto p-6 flex flex-col gap-5 ${dropdownOpen ? 'pb-32' : ''}`}>
          {/* Label combobox */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              {t.label_label || '推廣角度'}
            </label>
            {error ? (
              <div className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">
                {t.load_error || '無法載入推廣模板'}
              </div>
            ) : (
              <div className="relative" ref={comboboxRef}>
                <input
                  type="text"
                  value={inputLabel}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': primary }}
                  placeholder={t.label_placeholder || '選擇或輸入標籤...'}
                  disabled={loading}
                />
                {dropdownOpen && !loading && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredTemplates.map((tmpl) => (
                      <button
                        key={tmpl.id}
                        type="button"
                        onClick={() => handleSelectTemplate(tmpl)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
                      >
                        {tmpl.label}
                      </button>
                    ))}
                    {filteredTemplates.length === 0 && inputLabel.trim() && (
                      <div className="px-3 py-2 text-sm text-gray-400">
                        {t.new_label_hint || '按下複製後會自動建立新標籤'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Platform buttons */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              {t.select_platform || '選擇平台'}
            </label>
            <div className="flex gap-2 flex-wrap">
              {PLATFORMS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPlatform(p.key)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors cursor-pointer ${
                    platform === p.key
                      ? 'text-white border-transparent'
                      : 'text-gray-600 border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                  style={platform === p.key ? { backgroundColor: primary } : undefined}
                >
                  {p.emoji} {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Link preview */}
          {finalLink && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {t.link_preview || '最終連結'}
              </label>
              <div className="bg-gray-50 px-3 py-2 rounded-lg text-xs text-gray-500 break-all font-mono">
                {finalLink}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 px-5 py-4 border-t flex items-center justify-end gap-3">
          {copyError && (
            <span className="text-xs text-red-500 mr-auto">{t.copy_error || '複製失敗'}</span>
          )}
          <button
            type="button"
            onClick={handleCopyPlain}
            disabled={copyDone}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 cursor-pointer disabled:opacity-50"
          >
            {copyDone ? (t.copied || '已複製！') : (t.copy_plain || '直接複製')}
          </button>
          <button
            type="button"
            onClick={handleCopyTracked}
            disabled={!canCopyTracked || copying || copyDone}
            className="px-4 py-2 text-sm text-white rounded-lg font-medium disabled:opacity-50 cursor-pointer"
            style={{ backgroundColor: primary }}
          >
            {copying
              ? (t.copying || '複製中...')
              : isCopied
                ? (t.copied || '已複製！')
                : (t.copy_tracked || '複製推廣連結')
            }
          </button>
        </div>
      </div>
    </div>
  )
}
